import { Skeleton } from "@/components/ui/skeleton";
import { PII_MASK_CLASS } from "@/lib/analytics";
import { useCountries } from "@/queries/regions";
import { useProvinces } from "@/queries/regions/regions";
import { Input, Select } from "@medusajs/ui";
import { Form } from "@repo/design-system";
import { memo, useEffect, type ReactNode } from "react";
import type { UseFormReturn } from "react-hook-form";
import type { BaseAddressData } from "./address-schema";

/** Minimum form shape: all base address fields + optional address_name */
type AddressFormFieldsShape = BaseAddressData & { address_name?: string };

export interface AddressFormFieldsProps<
  T extends AddressFormFieldsShape = AddressFormFieldsShape
> {
  /** The react-hook-form instance. Must have the base address fields registered. */
  form: UseFormReturn<T>;
  /** Whether to show the address_name field. Default: true */
  showAddressName?: boolean;
  /** Content rendered before all address fields (e.g., save-for-later checkbox) */
  slotBefore?: ReactNode;
}

/**
 * Shared address form fields renderer.
 *
 * Renders the standard address fields (name, company, address lines, city,
 * postal code, province, country, phone) without a <form> tag, submit buttons,
 * or Form provider. The parent component owns the form instance and wraps
 * this in its own <Form> and <form>.
 *
 * Generic external interface preserves call-site type safety; internally
 * the form is narrowed to `AddressFormFieldsShape` so that react-hook-form's
 * `Path<T>` utility resolves field names correctly.
 */
function AddressFormFieldsImpl({
  form,
  showAddressName = true,
  slotBefore
}: AddressFormFieldsProps<AddressFormFieldsShape>) {
  const {
    watch,
    formState: { errors }
  } = form;
  const selectedCountry = watch("country_code");
  const { countries, isLoading: isLoadingCountries } = useCountries();
  const { provinces, isLoading: isLoadingProvinces } =
    useProvinces(selectedCountry);

  // Set meta field so the schema refinement knows whether province is required
  // and which codes are valid for the selected country
  useEffect(() => {
    form.setValue(
      "_provinces",
      provinces
        ? { type: provinces.type, codes: provinces.options.map((p) => p.code) }
        : undefined
    );
  }, [provinces, form]);

  return (
    <div className="space-y-4">
      {slotBefore}

      {/* Address Name */}
      {showAddressName && (
        <Form.Field
          control={form.control}
          name="address_name"
          render={({ field }) => (
            <Form.Item>
              <Form.Label>Address Name</Form.Label>
              <Form.Control>
                <Input placeholder="e.g., Home, Work, Office" {...field} />
              </Form.Control>
              <Form.ErrorMessage />
            </Form.Item>
          )}
        />
      )}

      {/* First Name / Last Name */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Form.Field
          control={form.control}
          name="first_name"
          render={({ field }) => (
            <Form.Item>
              <Form.Label>First Name</Form.Label>
              <Form.Control>
                <Input placeholder="First name" {...field} />
              </Form.Control>
              <Form.ErrorMessage />
            </Form.Item>
          )}
        />

        <Form.Field
          control={form.control}
          name="last_name"
          render={({ field }) => (
            <Form.Item>
              <Form.Label>Last Name</Form.Label>
              <Form.Control>
                <Input placeholder="Last name" {...field} />
              </Form.Control>
              <Form.ErrorMessage />
            </Form.Item>
          )}
        />
      </div>

      {/* Company */}
      <Form.Field
        control={form.control}
        name="company"
        render={({ field }) => (
          <Form.Item>
            <Form.Label optional>Company</Form.Label>
            <Form.Control>
              <Input placeholder="Company name" {...field} />
            </Form.Control>
            <Form.ErrorMessage />
          </Form.Item>
        )}
      />

      {/* Address Line 1 */}
      <Form.Field
        control={form.control}
        name="address_1"
        render={({ field }) => (
          <Form.Item>
            <Form.Label>Address Line 1</Form.Label>
            <Form.Control>
              <Input placeholder="Street address" {...field} />
            </Form.Control>
            <Form.ErrorMessage />
          </Form.Item>
        )}
      />

      {/* Address Line 2 */}
      <Form.Field
        control={form.control}
        name="address_2"
        render={({ field }) => (
          <Form.Item>
            <Form.Label optional>Address Line 2</Form.Label>
            <Form.Control>
              <Input placeholder="Apartment, suite, unit, etc." {...field} />
            </Form.Control>
            <Form.ErrorMessage />
          </Form.Item>
        )}
      />

      {/* City / Postal Code */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Form.Field
          control={form.control}
          name="city"
          render={({ field }) => (
            <Form.Item>
              <Form.Label>City</Form.Label>
              <Form.Control>
                <Input placeholder="City" {...field} />
              </Form.Control>
              <Form.ErrorMessage />
            </Form.Item>
          )}
        />

        <Form.Field
          control={form.control}
          name="postal_code"
          render={({ field }) => (
            <Form.Item>
              <Form.Label>Postcode</Form.Label>
              <Form.Control>
                <Input placeholder="Postcode" {...field} />
              </Form.Control>
              <Form.ErrorMessage />
            </Form.Item>
          )}
        />
      </div>

      {/* Province / Country */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Form.Field
          control={form.control}
          name="province"
          render={({ field: { ref, onChange, ...field } }) => (
            <Form.Item>
              <Form.Label optional={!provinces?.options.length}>
                {provinces?.type || "State / Region"}
              </Form.Label>
              <Form.Control>
                {isLoadingProvinces ? (
                  <Skeleton className="h-10 w-full rounded-md" />
                ) : !provinces?.options.length ? (
                  <Input
                    placeholder="State or Region"
                    ref={ref}
                    onChange={onChange}
                    {...field}
                  />
                ) : (
                  <Select {...field} onValueChange={onChange}>
                    {/* The chosen state is rendered as text in the trigger, which
                        `maskAllInputs` does not reach (ADR-0006). The option
                        list is the same fixed set for every salon, so it needs
                        no mask. */}
                    <Select.Trigger
                      ref={ref}
                      aria-invalid={!!errors[field.name]}
                      className={PII_MASK_CLASS}
                    >
                      <Select.Value
                        placeholder={`Select ${provinces.type.toLowerCase()}`}
                      />
                    </Select.Trigger>
                    <Select.Content className="z-[100]">
                      {provinces.options.map((province) => (
                        <Select.Item key={province.code} value={province.code}>
                          {province.name}
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select>
                )}
              </Form.Control>
              <Form.ErrorMessage />
            </Form.Item>
          )}
        />

        <Form.Field
          control={form.control}
          name="country_code"
          render={({ field: { ref, onChange, ...field } }) => (
            <Form.Item>
              <Form.Label>Country</Form.Label>
              <Form.Control>
                {isLoadingCountries ? (
                  <Skeleton className="h-10 w-full rounded-md" />
                ) : (
                  <Select {...field} onValueChange={onChange}>
                    <Select.Trigger
                      ref={ref}
                      aria-invalid={!!errors[field.name]}
                      className={PII_MASK_CLASS}
                    >
                      <Select.Value placeholder="Select country" />
                    </Select.Trigger>
                    <Select.Content className="z-[100]">
                      {countries.map((country) => (
                        <Select.Item
                          key={country.code}
                          value={country.code.toLowerCase()}
                        >
                          {country.name}
                        </Select.Item>
                      ))}
                    </Select.Content>
                  </Select>
                )}
              </Form.Control>
              <Form.ErrorMessage />
            </Form.Item>
          )}
        />
      </div>

      {/* Phone */}
      <Form.Field
        control={form.control}
        name="phone"
        render={({ field }) => (
          <Form.Item>
            <Form.Label optional>Phone</Form.Label>
            <Form.Control>
              <Input type="tel" placeholder="+61 (2) 123-4567" {...field} />
            </Form.Control>
            <Form.ErrorMessage />
          </Form.Item>
        )}
      />
    </div>
  );
}

// memo drops generics, so cast to restore the generic signature for call sites
export const AddressFormFields = memo(AddressFormFieldsImpl) as <
  T extends AddressFormFieldsShape
>(
  props: AddressFormFieldsProps<T>
) => React.JSX.Element;
